/**
 * ファイルダウンロード用の共通サービス。
 *
 * 本番障害（2026-07-27 西之山配水場作業所報告, issue #959）の再発防止が目的。
 *
 * PDF出力では presigned URL を `window.open(url, "_blank")` で開いていたが、
 * ブラウザは「ユーザー操作直後（transient user activation 中）」しか
 * ポップアップを許可しない（Chrome は約1秒、Safari は同期実行のみ）。
 * PDF生成は非同期通信を2往復挟むため実測 4〜30 秒かかっており、
 * window.open() は確実にブロックされていた。
 * さらに window.open() はブロックされても例外を投げず null を返すだけなので、
 * 「成功トーストは出るが PDF は出ない」という無言failになっていた。
 *
 * そのため window.open() は使わず、いったん blob として取得してから
 * <a download> でダウンロードさせる。
 * クロスオリジン（S3）の URL を直接 <a download> に渡すと download 属性が
 * 無視されるため、blob 化して同一オリジンの blob: URL にする必要がある。
 */

/**
 * ダウンロード（取得・保存）の失敗を表すエラー。
 *
 * 生成そのものの失敗と区別するために使う。
 * PDF出力では「サーバ側は成功しているのにユーザーには失敗に見える」状態が
 * 問い合わせの切り分けを困難にしたため（issue #959）、
 * 呼び出し側が両者を区別してメッセージを出し分けられるようにしている。
 */
export class DownloadError extends Error {
  constructor(message) {
    super(message);
    this.name = "DownloadError";
  }
}

/**
 * ファイル名に使えない文字を除去する
 * @param {string} name ファイル名
 * @returns {string} サニタイズ済みのファイル名
 */
export function sanitizeFileName(name) {
  // Windows/macOS で使用できない文字と制御文字を除去する
  // eslint-disable-next-line no-control-regex
  return String(name).replace(/[\\/:*?"<>|\x00-\x1f]/g, "_");
}

/**
 * URL からファイルを取得してダウンロードさせる
 *
 * @param {string} url ダウンロード対象の URL（S3 presigned URL 等）
 * @param {string} fileName 保存時のファイル名
 * @returns {Promise<void>} ダウンロード開始時に解決される
 * @throws {DownloadError} url が空、または取得に失敗した場合
 */
export async function downloadFileFromUrl(url, fileName) {
  if (!url) {
    throw new DownloadError("ダウンロードURLが指定されていません");
  }

  // presigned URL は署名済みのため、Authorization ヘッダー等を付けてはならない。
  // axios の共通インスタンスはインターセプターでトークンを付与するので、
  // ここでは素の fetch を使う。
  let response;
  try {
    response = await fetch(url);
  } catch (error) {
    // ネットワークエラー・CORSエラー等
    throw new DownloadError(`ファイルの取得に失敗しました: ${error.message}`);
  }
  if (!response.ok) {
    throw new DownloadError(`ファイルの取得に失敗しました: ${response.status}`);
  }

  const blob = await response.blob();
  downloadBlob(blob, fileName);
}

/**
 * Blob をダウンロードさせる
 *
 * API の応答に含まれるファイル（base64 で受け取った PDF 等）を保存させる用。
 * 同一オリジンの blob: URL を <a download> に渡すので、
 * window.open() と違ってポップアップブロックの影響を受けない（issue #959）。
 *
 * @param {Blob} blob ダウンロード対象
 * @param {string} fileName 保存時のファイル名
 */
export function downloadBlob(blob, fileName) {
  const objectUrl = window.URL.createObjectURL(blob);

  try {
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = sanitizeFileName(fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    // click() の直後に同期的に revoke すると、一部の Firefox / Safari で
    // ダウンロード開始前にURLが失効し中断されることがあるため、
    // 次のタスクまで解放を遅延させる
    setTimeout(() => window.URL.revokeObjectURL(objectUrl), 0);
  }
}

/**
 * base64 文字列を Blob に変換する
 *
 * @param {string} base64 base64 エンコードしたデータ
 * @param {string} type MIME タイプ（例: "application/pdf"）
 * @returns {Blob} 変換した Blob
 * @throws {DownloadError} base64 が空、または不正な場合
 */
export function base64ToBlob(base64, type) {
  if (!base64) {
    throw new DownloadError("ファイルのデータが空です");
  }
  let binary;
  try {
    binary = atob(base64);
  } catch (error) {
    throw new DownloadError(`ファイルのデータが不正です: ${error.message}`);
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type });
}

export default {
  downloadFileFromUrl,
  downloadBlob,
  base64ToBlob,
  sanitizeFileName,
  DownloadError,
};
